import { LogicalOperator } from 'constants/types';
import { FilterWell } from './utils';

export function computeIngredientFilterWells(
  plates: any[] | undefined,
  selectedIngredients: string[],
  ingredientFilterLogic: LogicalOperator,
): FilterWell[] {
  if (!selectedIngredients.length) {
    return [];
  }

  return (plates ?? []).flatMap((plate: any) =>
    (
      plate.ingredients?.wellList?.filter((well: any) => {
        const ingredientNames = (well.wellLayerList ?? []).flatMap((wellLayer: any) =>
          (wellLayer.wellLayerIngredientList ?? []).map(
            (ingredient: any) => ingredient?.ingredientStockIngredient?.longName,
          ),
        );
        const ingredientSet = new Set(ingredientNames);

        if (ingredientFilterLogic === LogicalOperator.And) {
          return selectedIngredients.every((name) => ingredientSet.has(name));
        }

        return selectedIngredients.some((name) => ingredientSet.has(name));
      }) ?? []
    ).map((well: any) => ({
      wellNumber: well.wellNumber,
      plateId: plate.plateId,
      hit: true,
    })),
  );
}

export function getUsedScoreGroupIds(plates: any[] | undefined): Set<number> {
  const groupIds = new Set<number>();

  (plates ?? []).forEach((plate: any) => {
    plate.plateScores?.forEach((group: any[]) => {
      group?.forEach((score) => groupIds.add(score.wellDropScoreGroupId));
    });
  });

  return groupIds;
}

export function computeScoreFilterWells(
  plates: any[] | undefined,
  selectedScoreIds: number[],
  scoreFilterLogic: LogicalOperator,
): FilterWell[] {
  if (!selectedScoreIds.length) {
    return [];
  }

  return (plates ?? []).flatMap((plate: any) => {
    const scoreIdsByDrop = new Map<
      string,
      { wellNumber: number; dropNumber?: number; ids: Set<number> }
    >();

    plate.plateScores?.forEach((group: any[]) => {
      group?.forEach((score) => {
        const key = `${score.wellNumber}-${score.dropNumber}`;
        if (!scoreIdsByDrop.has(key)) {
          scoreIdsByDrop.set(key, {
            wellNumber: score.wellNumber,
            dropNumber: score.dropNumber,
            ids: new Set<number>(),
          });
        }
        scoreIdsByDrop.get(key)?.ids.add(score.wellDropScoreId);
      });
    });

    return Array.from(scoreIdsByDrop.values())
      .filter(({ ids }) => {
        if (scoreFilterLogic === LogicalOperator.And) {
          return selectedScoreIds.every((id) => ids.has(id));
        }
        return selectedScoreIds.some((id) => ids.has(id));
      })
      .map(({ wellNumber, dropNumber }) => ({
        wellNumber,
        dropNumber,
        plateId: plate.plateId,
        hit: true,
      }));
  });
}
